import dayjs from "dayjs";
import * as XLSX from "xlsx";

const EXCEL_CONFIG = {
  dateFormat: "DD/MM/YYYY HH:mm",
  maxSheetNameLength: 31,
  fileExtension: ".xlsx",
};

function validateInputs(responseObj, datastreamList, fieldName) {
  if (!responseObj || typeof responseObj !== "object")
    throw new Error("responseObj is not valid");
  if (!Array.isArray(datastreamList))
    throw new Error("datastreamList must be an array");
  if (!fieldName || typeof fieldName !== "string")
    throw new Error("fieldName is not valid");
  if (Object.keys(responseObj).length === 0)
    throw new Error("No data to export");
}

function cleanSheetName(name) {
  return name
    // eslint-disable-next-line no-useless-escape
    .replace(/[\[\]\*/\\\?\:]/g, "")
    .slice(0, EXCEL_CONFIG.maxSheetNameLength);
}

function generateUniqueSheetName(baseName, usedNames) {
  let uniqueName = baseName;
  let counter = 1;
  while (usedNames.has(uniqueName)) {
    const suffix = `_${counter}`;
    uniqueName = baseName.slice(0, EXCEL_CONFIG.maxSheetNameLength - suffix.length) + suffix;
    counter++;
  }
  return uniqueName;
}

/**
 * Raggruppa i datastream per sensore fisico (deviceID).
 * Restituisce un array ordinato: un elemento per ogni sensore univoco.
 */
function groupDatastreamsBySensor(datastreamList, sensors) {
  const groups = {};
  datastreamList.forEach((ds) => {
    const deviceID = ds.deviceID || "unknown";
    if (!groups[deviceID]) {
      const sensor = sensors.find((s) => s.id_thing === deviceID);
      groups[deviceID] = {
        sensorLabel: sensor?.label || deviceID,
        datastreams: [],
      };
    }
    groups[deviceID].datastreams.push(ds);
  });
  return Object.values(groups);
}

/**
 * Crea i dati di un foglio per un singolo sensore.
 * Le colonne sono: timestamp + una colonna per ogni datastream del sensore.
 * Le righe vengono mergiate per timestamp (union di tutti i timestamp unici).
 */
function buildSensorSheetData(datastreams, responseObj, config) {
  // Raccoglie tutti i timestamp unici di tutti i datastream del sensore
  const allTimestamps = new Set();
  datastreams.forEach((ds) => {
    (responseObj[ds.name] || []).forEach(({ time_of_measure }) => {
      allTimestamps.add(time_of_measure);
    });
  });

  const sortedTimestamps = Array.from(allTimestamps).sort();

  // Mappa lookup: { datastreamName: { timestamp: value } }
  const lookups = {};
  datastreams.forEach((ds) => {
    lookups[ds.name] = {};
    (responseObj[ds.name] || []).forEach(({ time_of_measure, value }) => {
      lookups[ds.name][time_of_measure] = value;
    });
  });

  // Nomi delle colonne valore per ogni datastream
  const colNames = {};
  datastreams.forEach((ds) => {
    const unit = ds.unit_of_measurement;
    const label = ds.observed_property || ds.name;
    colNames[ds.name] = unit ? `${label} (${unit})` : label;
  });

  return sortedTimestamps.map((ts) => {
    const row = { timestamp: dayjs(ts).format(config.dateFormat) };
    datastreams.forEach((ds) => {
      row[colNames[ds.name]] = lookups[ds.name][ts] ?? "";
    });
    return row;
  });
}

/**
 * Crea i dati del foglio "Modello" dai dati previsionali.
 * Esporta solo i dataset con label e dati effettivi (esclude dataset vuoti delle linee verticali).
 */
function buildModelSheetData(modelsData, config) {
  if (!modelsData?.labels?.length || !modelsData?.datasets?.length) return [];

  const meaningfulDatasets = modelsData.datasets.filter(
    (ds) => ds.label && Array.isArray(ds.data) && ds.data.length > 0
  );

  return modelsData.labels.map((label, i) => {
    const row = { timestamp: dayjs(label).format(config.dateFormat) };
    meaningfulDatasets.forEach((ds) => {
      row[ds.label] = ds.data[i] ?? "";
    });
    return row;
  });
}

/**
 * Esporta i dati dei sensori in un file Excel.
 * Un foglio per ogni sensore fisico (raggruppando i suoi datastream come colonne).
 * Foglio "Modello" aggiunto opzionalmente se modelsData è disponibile.
 *
 * @param {Object} responseObj - { datastreamName: [{value, time_of_measure}] }
 * @param {Array} datastreamList - Lista datastream con deviceID, unit_of_measurement, observed_property
 * @param {string} fieldName - Nome del campo (usato nel nome file)
 * @param {Array} sensors - Sensori del campo [{id_thing, label}] per nominare i fogli
 * @param {Object|null} modelsData - Dati previsionali ({labels, datasets}) o null
 * @param {Object} config - Configurazioni opzionali
 */
export function exportToExcel(
  responseObj,
  datastreamList,
  fieldName,
  sensors = [],
  modelsData = null,
  config = {}
) {
  try {
    validateInputs(responseObj, datastreamList, fieldName);

    const finalConfig = { ...EXCEL_CONFIG, ...config };
    const workbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();

    // Un foglio per ogni sensore fisico
    const sensorGroups = groupDatastreamsBySensor(datastreamList, sensors);

    sensorGroups.forEach(({ sensorLabel, datastreams }) => {
      const sheetData = buildSensorSheetData(datastreams, responseObj, finalConfig);

      const cleanedName = cleanSheetName(sensorLabel);
      const uniqueName = generateUniqueSheetName(cleanedName, usedSheetNames);
      usedSheetNames.add(uniqueName);

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, uniqueName);
    });

    // Foglio modello opzionale
    if (modelsData?.labels?.length) {
      const modelSheetData = buildModelSheetData(modelsData, finalConfig);
      if (modelSheetData.length > 0) {
        const modelWorksheet = XLSX.utils.json_to_sheet(modelSheetData);
        XLSX.utils.book_append_sheet(workbook, modelWorksheet, "Modello");
      }
    }

    const fileName = `sensors_data_${fieldName}${finalConfig.fileExtension}`;
    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    console.error("Errore durante l'esportazione Excel:", error);
    throw new Error(`Impossibile esportare i dati in Excel: ${error.message}`);
  }
}
